import {createSelector} from "@reduxjs/toolkit";

import {CommonState} from "..";
import {Course, Lesson, Task} from "../../common/course";
import {combine} from "../../utils";
import {InitialState} from "./types";

export const selectCoursesReducer = (state: CommonState): InitialState => state.course;
export const selectCourses = (state: CommonState) => (
    combine(
        selectCoursesReducer,
        (courseState: InitialState) => Object.values(courseState.course)
    )(state)
);
export const selectCourseById = (state: CommonState, courseId: string): Course => combine(
    selectCoursesReducer,
    (courseState: InitialState) => courseState.course[courseId],
)(state);

export const selectLessonsByCourseId = createSelector(
    [
        (state: CommonState) => selectCoursesReducer(state),
        (state: CommonState, id: string) => id,
    ],
    (state: InitialState, id) => {
        return Object.values(state.lesson).filter(lesson => lesson.courseId === id); 
    }
);

export const selectTasksByCourseId = createSelector([
    (state) => state,
    (state: CommonState, id: string) => selectLessonsByCourseId(state, id)
], (state: CommonState, lessons: Lesson[]) => {
    return lessons.reduce((acc: Task[], curr: Lesson) => {
        return [...acc, ...selectTasksByLessonId(state, curr.id)];
    }, []);
});

export const selectLessonById = (state: CommonState, id: string): Lesson => combine(
    selectCoursesReducer,
    (courseState: InitialState) => courseState.lesson[id],
)(state); 

export const selectTasksByLessonId = createSelector(
    [
        (state: CommonState) => selectCoursesReducer(state),
        (state: CommonState, id: string) => id,
    ],
    (courseState: InitialState, id: string): Task[] => { 
        return Object.values(courseState.task).filter(task => task.lessonId === id)
    }
);

export const selectTaskById = (state: CommonState, id: string): Task => combine(
    selectCoursesReducer,
    (courseState: InitialState) => courseState.task[id],
)(state); 

export const selectTestById = createSelector([
    (state: CommonState) => selectCoursesReducer(state),
    (state: CommonState, id?: string) => id, 
], (courseState: InitialState, id?: string) => id ? courseState?.test[id] : undefined);

export const selectTestSubmitionById = createSelector([
    (state: CommonState) => selectCoursesReducer(state),
    (state: CommonState, id?: string) => id, 
], (courseState: InitialState, id?: string) => id ? courseState?.testSubmition[id] : undefined);